/**
 * Envelope + pagination adapters — CONTRACTS.md §1/§3/§5.
 *
 * The backend is NOT uniform (doc 02 §1): 7 controllers wrap in `SuccessResponseDTO<T>`,
 * `StatisticsController` returns bare DTOs, and `/api/auth/*` returns raw maps. Feature hooks
 * pass these selectors as TanStack Query `select` fns so feature code sees `T`, never the envelope.
 */

/** Shape A — matches core.model.response.success.SuccessResponseDTO<T>. */
// Fields are optional to stay assignment-compatible with the springdoc-generated SuccessResponseDTO* / PageResponse*
// types (springdoc emits every property optional). On a successful response `data` is present.
export interface SuccessResponseDTO<T> {
  httpStatusCode?: number; // e.g. 200 (httpStatus enum hidden via OpenApiCustomizer, doc 02 §5.1)
  data?: T | null;
  message?: string | null;
  messageCode?: string | null;
  timeStamp?: string | null; // ISO-8601 UTC
  httpStatus?: string | null;
}

/** Matches core.model.PageResponse<T> — verified: NO `pageSize` field (CONTRACTS.md §1).
 *  Optional fields for generated-type compatibility; the grid defaults missing values. */
export interface PageResponse<T> {
  rows?: T[] | null;
  pageNumber?: number; // long on the wire; 1-based
  totalPage?: number;
  totalElements?: number;
}

const isEnvelope = (v: unknown): v is SuccessResponseDTO<unknown> =>
  !!v && typeof v === 'object' && 'httpStatusCode' in v && 'data' in v;

/** Shape A -> data; shapes B/C -> passthrough. Base of every feature select. */
export const unwrap = <T>(res: unknown): T => (isEnvelope(res) ? (res.data as T) : (res as T));

// Canonical selectors — CONTRACTS.md §3. There is intentionally NO `selectPage` returning
// `{ rows, total, pageCount }`: that shape drops pageNumber/totalPage/totalElements the grid needs.

/** select: SuccessResponseDTO<T> -> T (detail; for a list endpoint returns the full PageResponse<T>). */
export const selectData = <T>(res: SuccessResponseDTO<T>): T => res.data as T;

/** select: SuccessResponseDTO<PageResponse<T>> -> T[] (bare row array; parity with AdminRepository .rows). */
export const selectRows = <T>(res: SuccessResponseDTO<PageResponse<T>>): T[] => res.data?.rows ?? [];

/** Shape B (StatisticsController) is already bare — pass through, no unwrap (CONTRACTS.md §3). */
export const selectBare = <T>(res: T): T => res;

// Single-arg pagination helpers — read the real PageResponse fields (CONTRACTS.md §5).
export const getNextPageParam = (last: PageResponse<unknown>): number | undefined => {
  const pn = last.pageNumber ?? 0; // 1-based current page
  const tp = last.totalPage ?? 0; // total page count
  return pn < tp ? pn + 1 : undefined; // a next page exists iff current < total
};

export const isEndReached = (last: PageResponse<unknown>): boolean =>
  (last.pageNumber ?? 0) >= (last.totalPage ?? 0);
